'use client';
import React, { useRef } from "react";
import SecondaryButton from "./SecondaryButton/SecondaryButton";
import { gsap } from "gsap";
import { useGSAP } from "@gsap/react";
import { LanguageType } from '@/lib/types';
import { useAnimatedNavigate } from '@/hooks/useAnimatedNavigation';

interface SubmitFormButtonProps {
    isLg: boolean;
    isLandingPage?: boolean;
    wrapperText?: string;
    wrapperBgColor?: string;
    onClick?: () => void;
    onReset?: () => void;
    disabled?: boolean;
    loading?: boolean;
    style?: "neutral" | "accent";
    isError?: boolean;
    language?: LanguageType;
    buttonText?: string;
    resetButtonText?: string;
    privacyText?: string;
}







// TODO: !!!!! add scroll trigger refresh during extension/retraction !!!!





export const SubmitFormButton: React.FC<SubmitFormButtonProps> = ({
    isLg,
    isLandingPage = false,
    wrapperText,
    wrapperBgColor = "bg-greens-green-2",
    onClick,
    onReset,
    disabled = false,
    loading = false,
    style = "neutral",
    isError = false,
    language = "english",
    buttonText,
    resetButtonText,
    privacyText
}) => {
    const wrapperContainerRef = useRef<HTMLDivElement>(null);
    const textContainerRef = useRef<HTMLDivElement>(null);
    const previousWrapperTextRef = useRef<string>('');
    const previousTextHeightRef = useRef<number>(0);
    const [displayedBgColor, setDisplayedBgColor] = React.useState(wrapperBgColor);
    const navigate = useAnimatedNavigate();

    // Set initial states on mount
    useGSAP(() => {
        const wrapperContainer = wrapperContainerRef.current;
        const textContainer = textContainerRef.current;

        if (!wrapperContainer || !textContainer) return;

        gsap.set(wrapperContainer, {
            height: 0,
            overflow: 'hidden'
        });

        gsap.set(textContainer, {
            opacity: 0
        });
    }, { dependencies: [], revertOnUpdate: true });

    // Handle text changes
    useGSAP(() => {
        const wrapperContainer = wrapperContainerRef.current;
        const textContainer = textContainerRef.current;

        if (!wrapperContainer || !textContainer) return;

        const currentText = wrapperText?.trim() || '';
        const previousText = previousWrapperTextRef.current;
        const hasCurrentText = currentText !== '';
        const hadPreviousText = previousText !== '';

        // Temporarily show text to measure height
        gsap.set(textContainer, { opacity: 1 });
        const textHeight = textContainer.getBoundingClientRect().height;
        gsap.set(textContainer, { opacity: hadPreviousText ? 1 : 0 });

        if (!hadPreviousText && hasCurrentText) {
            // Case 1: No message → Show message
            // Update color immediately when opening
            setDisplayedBgColor(wrapperBgColor);
            // First extend container, then show text
            const showTl = gsap.timeline();

            showTl
                .to(wrapperContainer, {
                    height: textHeight,
                    duration: 0.4,
                    ease: "none"
                })
                .to(textContainer, {
                    opacity: 1,
                    duration: 0.3,
                    ease: "none"
                });

            // Store the current text height
            previousTextHeightRef.current = textHeight;

        } else if (hadPreviousText && !hasCurrentText) {
            // Case 2: Show message → No message
            // Keep the previous bg color during close animation
            // First hide text, then retract container
            // Use stored height to maintain visual height during animation
            const hideTl = gsap.timeline();
            const storedHeight = previousTextHeightRef.current;
            const textElement = textContainer.querySelector('p');

            hideTl
                // Set container to stored height immediately (maintains visual height even with no content)
                .set(wrapperContainer, {
                    height: storedHeight
                })
                // Set text content back to previous text so it can fade out smoothly
                .call(() => {
                    if (textElement) {
                        textElement.textContent = previousText;
                    }
                })
                .to(textContainer, {
                    opacity: 0,
                    duration: 0.3,
                    ease: "none"
                })
                .to(wrapperContainer, {
                    height: 0,
                    duration: 0.4,
                    ease: "none",
                    onComplete: () => {
                        // Only update color after wrapper is fully closed
                        setDisplayedBgColor(wrapperBgColor);
                    }
                });

        } else if (hadPreviousText && hasCurrentText && currentText !== previousText) {
            // Case 3: Change message content - update color immediately
            setDisplayedBgColor(wrapperBgColor);
            // Just fade out and fade in new text, keep container extended
            const changeTl = gsap.timeline();

            // Adjust height if needed
            const currentHeight = wrapperContainer.getBoundingClientRect().height;

            if (Math.abs(textHeight - currentHeight) > 2) {
                // Height change needed
                changeTl
                    .to(textContainer, {
                        opacity: 0,
                        duration: 0.2,
                        ease: "none"
                    })
                    .to(wrapperContainer, {
                        height: textHeight,
                        duration: 0.3,
                        ease: "none"
                    }, "-=0.1")
                    .to(textContainer, {
                        opacity: 1,
                        duration: 0.2,
                        ease: "none"
                    });
            } else {
                // No height change, just swap text
                changeTl
                    .to(textContainer, {
                        opacity: 0,
                        duration: 0.15,
                        ease: "none"
                    })
                    .to(textContainer, {
                        opacity: 1,
                        duration: 0.15,
                        ease: "none"
                    });
            }

            // Store the current text height
            previousTextHeightRef.current = textHeight;
        }

        // Update previous text reference
        previousWrapperTextRef.current = currentText;

    }, { dependencies: [wrapperText, wrapperBgColor], revertOnUpdate: true });

    // Parse privacy text and convert "Privacy Policy" / "سياسة الخصوصية" to links
    const parsePrivacyText = (text: string | undefined): React.ReactNode => {
        if (!text) return null;
        
        const privacyPolicyEnglish = "Privacy Policy";
        const privacyPolicyArabic = "سياسة الخصوصية";
        
        // Check if text contains English privacy policy
        if (text.includes(privacyPolicyEnglish)) {
            const parts = text.split(privacyPolicyEnglish);
            return (
                <>
                    {parts[0]}
                    <span
                        // to="/legal" 
                        onClick={() => navigate('/legal')}
                        className="underline text-text-quinary hover:text-text-tertiary transition-colors cursor-pointer"
                    >
                        {privacyPolicyEnglish}
                    </span>
                    {parts[1]}
                </>
            );
        }
        
        // Check if text contains Arabic privacy policy
        if (text.includes(privacyPolicyArabic)) {
            const parts = text.split(privacyPolicyArabic);
            return (
                <>
                    {parts[0]}
                    <span
                        // to="/legal" 
                        onClick={() => navigate('/legal')}
                        className="underline text-text-quinary hover:text-text-tertiary transition-colors cursor-pointer"
                    >
                        {privacyPolicyArabic}
                    </span>
                    {parts[1]}
                </>
            );
        }
        
        // No privacy policy text found, return as is
        return text;
    };

    return (
        <div className="w-full flex flex-col gap-[0.75rem]">
            <div
                style={{
                    borderRadius: isLg ? '32px' : '16px',
                }}
                    // ${disabled ? 'bg-bg-disabled' : wrapperBgColor}
                className={`
                    ${displayedBgColor}
                    w-full flex flex-col
                    outline-main outline-stroke-soft-200
                `}
            >
                <SecondaryButton
                    size={isLg ? 64 : 52}
                    text={buttonText || (language === 'arabic' ? "الانضمام إلى قائمة الانتظار" : "Join the waitlist")}
                    onClick={onClick}
                    disabled={disabled}
                    loading={loading}
                    style={style}
                    isLandingPage={isLandingPage}
                    customOutline={
                        style === 'accent' && isError && wrapperText && !disabled
                            ? 'outline-state-error-dark hover:outline-state-error-dark focus:outline-state-error-dark'
                            : style === 'accent' && !isError && wrapperText && !disabled
                            ? 'outline-greens-green-3 hover:outline-greens-green-3 focus:outline-greens-green-3'
                            : undefined
                    }
                />

                {/* Wrapper container for background extension */}
                <div
                    ref={wrapperContainerRef}
                    className="w-full"
                >
                    {/* Text container for content animation */}
                    <div
                        ref={textContainerRef}
                        className="px-[0.75rem] py-[0.75rem]"
                    >
                        <p className="text-16--alexandria text-text-reversed text-center">
                            {wrapperText || ''}
                        </p>
                    </div>
                </div>
            </div>

            {wrapperText ? 
            <button 
                className="cursor-pointer text-14--alexandria text-text-quinary text-center"
                onClick={onReset}
            >
                {resetButtonText || (language === 'arabic' ? "إعادة تعيين النموذج" : "Reset the form")}
            </button>
            : <p className="text-14--alexandria text-text-quinary text-center">
                {parsePrivacyText(privacyText) || parsePrivacyText(language === 'arabic' ? "بياناتك محمية. لن نشارك معلوماتك مع أي جهة." : "Your data is protected. We'll never share your info with anyone.")}
            </p>}
        </div>
    );
};
